import React, {
    FormEvent,
    ReactElement, useEffect, useState,
} from "react";
import { Form } from "react-bootstrap";
import CodeBlock from "../../../../../components/code-block/CodeBlock";
import Editor from "../../../../../components/editor/Editor";
import IconUploader from "../../../../../components/pickers/icon-uploader/IconUploader";
import TagList from "../../../../../components/tag-list/TagList";
import TimedMessage from "../../../../../components/timed-message/TimedMessage";
import { GetSettingsDocument, useSaveSettingsMutation } from "../../../../../__generated__/api";
import { SettingsProps } from "../../base";

export default function ChannelSettings(props: SettingsProps): ReactElement {
    const [save] = useSaveSettingsMutation();
    const [isSaved, setIsSaved] = useState(false);

    const [allowedHosts, setAllowedHosts] = useState(() => props.settings?.channelWebChat?.allowedHosts ?? []);
    const [avatarUrl, setAvatarUrl] = useState(() => props.settings?.channelWebChat?.avatarUrl);
    const [name, setName] = useState(() => props.settings?.channelWebChat?.name);
    const [welcomeMessage, setWelcomeMessage] = useState(() => props.settings?.channelWebChat?.welcomeMessage);

    useEffect(() => {
        setAllowedHosts(props.settings?.channelWebChat?.allowedHosts ?? []);
        setAvatarUrl(props?.settings?.channelWebChat?.avatarUrl);
        setName(props?.settings?.channelWebChat?.name);
        setWelcomeMessage(props?.settings?.channelWebChat?.welcomeMessage);
    }, [props.settings]);

    const onSubmit = (event: FormEvent) => {
        event.preventDefault();
        (async () => {
            await save({
                refetchQueries: [
                    { query: GetSettingsDocument },
                ],
                variables: {
                    channelWebChat: {
                        allowedHosts,
                        avatarUrl,
                        name: name ?? "",
                        welcomeMessage,
                        isReferrerCheckRequired: allowedHosts.isEmpty,
                    },
                },
            });
            setIsSaved(true);
        })();
    };

    return (
        <form className="flex-grow-1" onSubmit={onSubmit}>

            <TimedMessage shouldShow={isSaved} onClose={() => setIsSaved(false)}>
                Chat settings saved
            </TimedMessage>
            <div className="border p-3 rounded mb-5 bg-white">
                <h5 className="mb-3 text-light">
                    Chat Settings
                </h5>

                <Form.Group className="mb-3">
                    <Form.Label className="fw-bolder d-block mb-0">Avatar</Form.Label>
                    <Form.Text className="mb-2 d-block">
                        The image to use as the avatar image when showing up for support agent.
                    </Form.Text>
                    <IconUploader value={avatarUrl as string} onChange={icon => setAvatarUrl(icon)} shouldStore />

                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Label className="fw-bolder">Name</Form.Label>
                    <Form.Control
                        value={name}
                        onChange={value => setName(value.target.value)}
                        type="text"
                        placeholder="Configuration name"
                    />
                    <Form.Text>
                        The name of the chat configuration.
                    </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Label className="fw-bolder">Welcome Message</Form.Label>

                    <Editor
                        className="border border-gray rounded"
                        text={welcomeMessage ?? ""}
                        onChange={msg => setWelcomeMessage(msg)}
                        placeholder="Hello and welcome!"
                    />
                    <Form.Text>
                        The default message a user sees when they open the chat.
                    </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Label className="fw-bolder">Allowed Hosts</Form.Label>
                    <TagList
                        value={allowedHosts}
                        onChange={value => setAllowedHosts(value)}
                        canNotHaveDuplicates
                        placeholder="Add host"
                        type="url"
                    />
                    <Form.Text>
                        Which websites or hostnames should the chat be allowed to be served from?
                    </Form.Text>
                </Form.Group>

            </div>

            <div className="border p-3 rounded bg-white mb-4">
                <div>
                    Your web script
                    <CodeBlock shouldWrap code={props.settings?.channelWebChat?.chatScript} className="my-2" />
                    <small>
                        This is an automatically generated script which, when added to your website, will display
                        a chat widget in the bottom right hand corner. Copy this javascript block
                        to your web page where you want to include chat.
                    </small>
                </div>
            </div>

            <div className="d-flex justify-content-end mt-3 fixed-bottom border-top p-3 bg-white">
                <button type="submit" className="btn btn-primary">
                    Save
                </button>
            </div>

        </form>
    );
}
